import { useEditorState, type Editor } from "@tiptap/react";
import {
  Bold,
  Code,
  ImagePlus,
  Italic,
  List,
  ListOrdered,
  Minus,
  Quote,
  Redo2,
  RemoveFormatting,
  Strikethrough,
  Subscript,
  Superscript,
  TextAlignCenter,
  TextAlignEnd,
  TextAlignJustify,
  TextAlignStart,
  Underline,
  Undo2,
} from "lucide-react";
import type { ComponentType } from "react";
import { BlockTypeMenu, type BlockType } from "./BlockTypeMenu";
import { ColorPopover } from "./ColorPopover";
import { LinkPopover } from "./LinkPopover";
import { TableMenu } from "./TableMenu";
import { ToolbarButton, ToolbarSeparator } from "./ToolbarButton";

type Align = "left" | "center" | "right" | "justify";

const ALIGNS: { value: Align; label: string; icon: ComponentType }[] = [
  { value: "left", label: "Sola hizala", icon: TextAlignStart },
  { value: "center", label: "Ortala", icon: TextAlignCenter },
  { value: "right", label: "Sağa hizala", icon: TextAlignEnd },
  { value: "justify", label: "İki yana yasla", icon: TextAlignJustify },
];

function colorAttr(editor: Editor, mark: string): string | null {
  const value: unknown = editor.getAttributes(mark).color;
  return typeof value === "string" && value ? value : null;
}

function blockTypeOf(editor: Editor): BlockType {
  if (editor.isActive("codeBlock")) return "code";
  for (const level of [1, 2, 3, 4, 5, 6] as const) {
    if (editor.isActive("heading", { level })) return `h${level}`;
  }
  return "p";
}

export function RichTextToolbar({
  editor,
  onInsertImage,
}: {
  editor: Editor;
  // Verilirse görsel ekleme düğmesi gösterilir.
  onInsertImage?: () => void;
}) {
  // TipTap 3 her işlemde bileşeni yeniden çizmez; araç çubuğunun aktif durumları
  // yalnızca değiştiklerinde güncellensin diye seçici ile okunur.
  const s = useEditorState({
    editor,
    selector: ({ editor: e }) => ({
      blockType: blockTypeOf(e),
      bold: e.isActive("bold"),
      italic: e.isActive("italic"),
      underline: e.isActive("underline"),
      strike: e.isActive("strike"),
      code: e.isActive("code"),
      subscript: e.isActive("subscript"),
      superscript: e.isActive("superscript"),
      link: e.isActive("link"),
      color: colorAttr(e, "textStyle"),
      highlight: e.isActive("highlight") ? (colorAttr(e, "highlight") ?? "#fef08a") : null,
      align: (ALIGNS.find((a) => e.isActive({ textAlign: a.value }))?.value ?? "left") as Align,
      bulletList: e.isActive("bulletList"),
      orderedList: e.isActive("orderedList"),
      blockquote: e.isActive("blockquote"),
      inTable: e.isActive("table"),
      canUndo: e.can().undo(),
      canRedo: e.can().redo(),
    }),
  });

  const chain = () => editor.chain().focus();

  return (
    <div
      role="toolbar"
      aria-label="Metin biçimlendirme"
      className="flex flex-wrap items-center gap-0.5 border-b border-input bg-muted/30 px-1.5 py-1"
    >
      <ToolbarButton label="Geri al (Ctrl+Z)" onClick={() => chain().undo().run()} disabled={!s.canUndo}>
        <Undo2 />
      </ToolbarButton>
      <ToolbarButton label="Yinele (Ctrl+Shift+Z)" onClick={() => chain().redo().run()} disabled={!s.canRedo}>
        <Redo2 />
      </ToolbarButton>

      <ToolbarSeparator />
      <BlockTypeMenu editor={editor} value={s.blockType} />

      <ToolbarSeparator />
      <ToolbarButton label="Kalın (Ctrl+B)" active={s.bold} onClick={() => chain().toggleBold().run()}>
        <Bold />
      </ToolbarButton>
      <ToolbarButton label="İtalik (Ctrl+I)" active={s.italic} onClick={() => chain().toggleItalic().run()}>
        <Italic />
      </ToolbarButton>
      <ToolbarButton
        label="Altı çizili (Ctrl+U)"
        active={s.underline}
        onClick={() => chain().toggleUnderline().run()}
      >
        <Underline />
      </ToolbarButton>
      <ToolbarButton
        label="Üstü çizili (Ctrl+Shift+S)"
        active={s.strike}
        onClick={() => chain().toggleStrike().run()}
      >
        <Strikethrough />
      </ToolbarButton>
      <ToolbarButton label="Satır içi kod (Ctrl+E)" active={s.code} onClick={() => chain().toggleCode().run()}>
        <Code />
      </ToolbarButton>
      <ToolbarButton
        label="Alt simge (Ctrl+,)"
        active={s.subscript}
        onClick={() => chain().toggleSubscript().run()}
      >
        <Subscript />
      </ToolbarButton>
      <ToolbarButton
        label="Üst simge (Ctrl+.)"
        active={s.superscript}
        onClick={() => chain().toggleSuperscript().run()}
      >
        <Superscript />
      </ToolbarButton>

      <ToolbarSeparator />
      <ColorPopover editor={editor} mode="color" current={s.color} />
      <ColorPopover editor={editor} mode="highlight" current={s.highlight} />
      <LinkPopover editor={editor} active={s.link} />

      <ToolbarSeparator />
      {ALIGNS.map((a) => (
        <ToolbarButton
          key={a.value}
          label={a.label}
          active={s.align === a.value}
          onClick={() => chain().setTextAlign(a.value).run()}
        >
          <a.icon />
        </ToolbarButton>
      ))}

      <ToolbarSeparator />
      <ToolbarButton
        label="Madde listesi"
        active={s.bulletList}
        onClick={() => chain().toggleBulletList().run()}
      >
        <List />
      </ToolbarButton>
      <ToolbarButton
        label="Numaralı liste"
        active={s.orderedList}
        onClick={() => chain().toggleOrderedList().run()}
      >
        <ListOrdered />
      </ToolbarButton>
      <ToolbarButton label="Alıntı" active={s.blockquote} onClick={() => chain().toggleBlockquote().run()}>
        <Quote />
      </ToolbarButton>
      <ToolbarButton label="Yatay çizgi" onClick={() => chain().setHorizontalRule().run()}>
        <Minus />
      </ToolbarButton>
      <TableMenu editor={editor} inTable={s.inTable} />
      {onInsertImage && (
        <ToolbarButton label="Yazının içine görsel ekle" onClick={onInsertImage}>
          <ImagePlus />
        </ToolbarButton>
      )}

      <ToolbarSeparator />
      <ToolbarButton
        label="Biçimlendirmeyi temizle"
        onClick={() => chain().unsetAllMarks().clearNodes().unsetTextAlign().run()}
      >
        <RemoveFormatting />
      </ToolbarButton>
    </div>
  );
}
